import { parseDate } from '../../utils/dateUtils';
import { isValid, differenceInDays } from 'date-fns';

/**
 * Abstract base class for all predictors
 * Provides common functionality and interface for prediction implementations
 */
export class BasePredictor {
  constructor(config = {}) {
    if (this.constructor === BasePredictor) {
      throw new Error("BasePredictor is an abstract class and cannot be instantiated directly");
    }
    
    this.config = config;
  }

  /**
   * Validates that the data and specified columns are valid for prediction
   * @param {Array} data - The data array
   * @param {string} xAxis - The x-axis column name
   * @param {string} yAxis - The y-axis column name
   * @returns {Object} - { valid: boolean, error?: string }
   */
  validateData(data, xAxis, yAxis) {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return { valid: false, error: 'No data provided for prediction' };
    }

    if (!xAxis || !yAxis) {
      return { valid: false, error: 'Both x-axis and y-axis must be specified' };
    }

    // Check if columns exist in data
    const firstRow = data[0];
    if (!firstRow.hasOwnProperty(xAxis)) {
      return { valid: false, error: `Column "${xAxis}" not found in data` };
    }
    if (!firstRow.hasOwnProperty(yAxis)) {
      return { valid: false, error: `Column "${yAxis}" not found in data` };
    }

    // Check if we have enough data points
    if (data.length < 2) {
      return { valid: false, error: 'At least 2 data points are required for prediction' };
    }

    return { valid: true };
  }

/**
 * Extracts x,y pairs from the data for time series analysis
 * Automatically aggregates duplicate time points if detected
 * @param {Array} data - The data array
 * @param {string} xAxis - The x-axis column name
 * @param {string} yAxis - The y-axis column name
 * @param {string} aggregationMethod - Method to aggregate duplicates ('average', 'sum', 'min', 'max')
 * @returns {Array} - Array of {x, y} objects with parsed values
 */
extractTimeSeries(data, xAxis, yAxis, aggregationMethod = 'average') {
  let series = [];  // Changed from const to let
  let detectedDateFormat = null; // Store the detected format
  
  // First, extract all valid points
  for (const row of data) {
    // Skip rows with null/undefined values
    if (row[xAxis] == null || row[yAxis] == null) {
      continue;
    }

    // Extract the actual value (handle both simple values and object format)
    let xValue = row[xAxis];
    let yValue = row[yAxis];
    
    // Handle nested display/sort value structure
    if (typeof xValue === 'object' && xValue !== null) {
      xValue = xValue.displayValue || xValue.sortValue || xValue.value || xValue;
    }
    if (typeof yValue === 'object' && yValue !== null) {
      yValue = yValue.sortValue || yValue.displayValue || yValue.value || yValue;
    }

    // Parse numeric y-value
    const numericY = typeof yValue === 'number' ? yValue : parseFloat(yValue);
    
    if (!isNaN(numericY)) {
      series.push({
        x: xValue,
        y: numericY,
        originalX: row[xAxis],
        originalY: row[yAxis]
      });
    }
  }

  // Safety check for series length
  if (series.length === 0) {
    console.warn('No valid data points found in time series');
    return [];
  }

  // Check if x-axis appears to be dates/timestamps
  if (series.length > 0 && this.looksLikeTimeSeries(series[0].x)) {
    // For time series methods with resolution request, aggregate first
    if (this.config.resolution && ['weekly', 'monthly', 'yearly'].includes(this.config.resolution)) {
      console.log(`Aggregating data to ${this.config.resolution} resolution`);
      series = this.aggregateToResolution(series, this.config.resolution, aggregationMethod);
    }
    
    // Check for duplicates (only if not already aggregated)
    const uniqueKeys = new Set(series.map(point => point.x));
    
    if (uniqueKeys.size < series.length) {
      console.log(`Detected duplicate time points. Aggregating ${series.length} points to ${uniqueKeys.size} unique values using ${aggregationMethod}`);
      
      // Aggregate duplicate time points
      const aggregatedMap = new Map();
      
      for (const point of series) {
        const key = point.x;
        
        if (aggregatedMap.has(key)) {
          aggregatedMap.get(key).values.push(point.y);
        } else {
          aggregatedMap.set(key, {
            x: point.x,
            values: [point.y],
            originalX: point.originalX,
            originalY: point.originalY
          });
        }
      }
      
      // Convert back to array with aggregated values
      return Array.from(aggregatedMap.values()).map(item => {
        let aggregatedY;
        
        switch (aggregationMethod) {
          case 'sum':
            aggregatedY = item.values.reduce((sum, val) => sum + val, 0);
            break;
          case 'min':
            aggregatedY = Math.min(...item.values);
            break;
          case 'max':
            aggregatedY = Math.max(...item.values);
            break;
          case 'average':
          default:
            aggregatedY = item.values.reduce((sum, val) => sum + val, 0) / item.values.length;
            break;
        }
        
        return {
          x: item.x,
          y: aggregatedY,
          originalX: item.originalX,
          originalY: item.originalY,
          aggregatedCount: item.values.length
        };
      }).sort((a, b) => {
        // Sort by x value (dates will sort correctly as strings in ISO format)
        // Parse dates properly for sorting
        const dateA = new Date(a.x);
        const dateB = new Date(b.x);
        return dateA - dateB;      });
    }
  }

  return series;
}

/**
 * Helper method to check if a value looks like a time series x-axis
 * @param {any} value - Value to check
 * @returns {boolean}
 */
looksLikeTimeSeries(value) {
  if (!value) return false;
  
  const stringValue = String(value);
  
  // Check common date/time patterns
  const timePatterns = [
    /^\d{4}-\d{2}-\d{2}/, // ISO date
    /^\d{2}\/\d{2}\/\d{4}/, // DD/MM/YYYY or MM/DD/YYYY
    /^\d{4}\/\d{2}\/\d{2}/, // YYYY/MM/DD
    /^(19|20)\d{2}/, // Years starting with 19 or 20
    /T\d{2}:\d{2}:\d{2}/, // ISO datetime with time
  ];
  
  return timePatterns.some(pattern => pattern.test(stringValue));
}

  /**
   * Generates future dates based on the last date and prediction target
   * @param {any} lastDate - The last date in the series
   * @param {string} predictUntil - The target date to predict until
   * @param {Array} data - The original data (for frequency detection)
   * @returns {Array} - Array of future x values
   */
  generateFutureDates(lastDate, predictUntil, data) {
    // This is a simplified implementation
    // Subclasses can override for more sophisticated date generation
    const futureDates = [];
    
    // For now, just generate placeholder future points
    // Real implementation would analyze the frequency and generate appropriate dates
    const numFuturePoints = this.config.numPredictions || 10;
    
    for (let i = 1; i <= numFuturePoints; i++) {
      futureDates.push(`Future_${i}`);
    }
    
    return futureDates;
  }

  /**
   * Formats the prediction output to standard structure
   * @param {Array} predictions - Array of predicted values
   * @param {string} xAxis - The x-axis column name
   * @param {string} yAxis - The y-axis column name
   * @returns {Array} - Formatted prediction data
   */
  formatPredictionOutput(predictions, xAxis, yAxis, sampleRow = null) {
    // Check if all historical y values are non-negative
    const preventNegative = this.config.preventNegative !== false; // Default true
    
    return predictions.map((pred, index) => {
      const formatted = {};
      
      // Apply safety check for negative values
      let yValue = pred.y;
      if (preventNegative && yValue < 0) {
        console.warn(`Preventing negative prediction: ${yValue}, setting to 0`);
        yValue = 0;
      }
      
      // Also check confidence intervals if they exist
      let lowerBound = pred.confidenceLower;
      let upperBound = pred.confidenceUpper;
      
      if (preventNegative && lowerBound !== undefined && lowerBound < 0) {
        lowerBound = 0;
      }
      
      // If we have a sample row, copy all columns with default values
      if (sampleRow) {
        Object.keys(sampleRow).forEach(key => {
          if (key === xAxis) {
            formatted[key] = pred.x;
          } else if (key === yAxis) {
            formatted[key] = yValue; // Use the safety-checked value
          } else {
            // Set other columns to null or 'N/A' based on type
            formatted[key] = typeof sampleRow[key] === 'string' ? 'N/A' : null;
          }
        });
      } else {
        // Fallback to minimal structure
        formatted[xAxis] = pred.x;
        formatted[yAxis] = pred.y;
      }
      
      return formatted;
    });
  }

  /**
   * Abstract method that must be implemented by subclasses
   * @param {Array} data - The data to analyze
   * @param {string} xAxis - The x-axis column name
   * @param {string} yAxis - The y-axis column name
   * @returns {Object} - { predictions: Array, metadata: Object }
   */
  predict(data, xAxis, yAxis) {
    throw new Error("predict() method must be implemented by subclass");
  }


  /**
   * Calculate number of predictions needed to reach target date
   * @param {Date} lastDate - Last date in the data
   * @param {string|Date} predictUntil - Target date to predict until
   * @param {string} frequency - Detected frequency ('daily', 'weekly', 'monthly', 'yearly')
   * @param {number} averageInterval - Average interval in days
   * @returns {number} - Number of predictions needed
   */
  calculatePredictionsNeeded(lastDate, predictUntil, frequency, averageInterval) {
    if (!predictUntil) {
      return this.config.numPredictions || 10;
    }

    // Parse predictUntil if it's a string
    const endDate = typeof predictUntil === 'string' ? parseDate(predictUntil) : predictUntil;
    
    if (!endDate || !isValid(endDate) || endDate <= lastDate) {
      return this.config.numPredictions || 10;
    }

    // Calculate days difference
    const daysDiff = Math.abs(differenceInDays(endDate, lastDate));
    
    // Calculate interval in days based on frequency
    let intervalDays;
    switch (frequency) {
      case 'daily':
        intervalDays = 1;
        break;
      case 'weekly':
        intervalDays = 7;
        break;
      case 'monthly':
        intervalDays = 30;
        break;
      case 'yearly':
        intervalDays = 365;
        break;
      default:
        intervalDays = Math.round(averageInterval) || 1;
    }

    // Calculate number of predictions needed
    const predictionsNeeded = Math.ceil(daysDiff / intervalDays);
    
    console.log(`Need ${predictionsNeeded} ${frequency} predictions to reach ${predictUntil}`);
    
    // Apply safety limit
    const maxAllowed = 365;
    if (predictionsNeeded > maxAllowed) {
      console.warn(`Limiting predictions from ${predictionsNeeded} to ${maxAllowed}`);
      return maxAllowed;
    }

    return predictionsNeeded;
  }

  /**
   * Aggregates time series data to specified resolution
   * @param {Array} series - Time series data
   * @param {string} resolution - Target resolution ('weekly', 'monthly', 'yearly')
   * @param {string} aggregationMethod - Method to aggregate ('sum', 'average', 'max', 'min')
   * @returns {Array} - Aggregated data
   */
  aggregateToResolution(series, resolution, aggregationMethod = 'sum') {
    const aggregatedData = new Map();
    
    series.forEach(point => {
      const date = parseDate(point.x);
      if (!date) return;
      
      let periodKey;
      
      switch (resolution) {
        case 'weekly':
          // Get start of week (Monday)
          const dayOfWeek = date.getDay();
          const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() + daysToMonday);
          weekStart.setHours(0, 0, 0, 0);
          periodKey = weekStart.toISOString().split('T')[0];
          break;
          
        case 'monthly':
          // Get start of month
          const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
          periodKey = monthStart.toISOString().split('T')[0];
          break;
          
        case 'yearly':
          // Get start of year
          const yearStart = new Date(date.getFullYear(), 0, 1);
          periodKey = yearStart.toISOString().split('T')[0];
          break;
          
        default:
          periodKey = point.x; // No aggregation
      }
      
      if (!aggregatedData.has(periodKey)) {
        aggregatedData.set(periodKey, {
          x: periodKey,
          values: [],
          originalX: point.originalX,
          dates: []
        });
      }
      
      aggregatedData.get(periodKey).values.push(point.y);
      aggregatedData.get(periodKey).dates.push(date);
    });
    
    // Aggregate values
    return Array.from(aggregatedData.values()).map(period => {
      let aggregatedY;
      
      switch (aggregationMethod) {
        case 'sum':
          aggregatedY = period.values.reduce((sum, val) => sum + val, 0);
          break;
        case 'average':
          aggregatedY = period.values.reduce((sum, val) => sum + val, 0) / period.values.length;
          break;
        case 'max':
          aggregatedY = Math.max(...period.values);
          break;
        case 'min':
          aggregatedY = Math.min(...period.values);
          break;
        default:
          aggregatedY = period.values.reduce((sum, val) => sum + val, 0);
      }
      
      // For display, use the middle date of the period for better visualization
      const middleDate = period.dates[Math.floor(period.dates.length / 2)];
      
      return {
        x: period.x,
        y: aggregatedY,
        originalX: period.originalX,
        aggregated: true,
        aggregationType: resolution,
        dataPoints: period.values.length,
        displayDate: middleDate
      };
    }).sort((a, b) => new Date(a.x) - new Date(b.x));
  }

  /**
   * Check if historical data is all non-negative
   * @param {Array} timeSeries - Time series data
   * @returns {boolean} - True if all values are >= 0
   */
  checkHistoricalPositive(timeSeries) {
    return timeSeries.every(point => point.y >= 0);
  }

  /*───────────────────────────────────────────────────────────────
  QUICK HOLD-OUT BACK-TEST
  Splits off the last horizon points, fits the model on the remainder,
  forecasts that horizon and returns sMAPE & RMSE.
  ───────────────────────────────────────────────────────────────*/
  backtest(timeSeries, horizon = Math.max(1, Math.floor(timeSeries.length * 0.2))) {
    if (timeSeries.length <= horizon + 3) {
      return { smape: Infinity, rmse: Infinity }; // not enough data
    }

    // ---------- split ----------
    const train = timeSeries.slice(0, -horizon).map(p => p.y);
    const test = timeSeries.slice(-horizon).map(p => p.y);

    // ---------- run this very predictor ----------
    const { forecasts } = this.forecast(train, horizon);

    // ---------- error metrics ----------
    let smapeSum = 0, seSum = 0, n = Math.min(test.length, forecasts.length);
    for (let i = 0; i < n; i++) {
      const f = forecasts[i], a = test[i];
      smapeSum += Math.abs(f - a) / ((Math.abs(f) + Math.abs(a)) / 2);
      seSum += Math.pow(f - a, 2);
    }

    return {
      smape: (smapeSum / n) * 100,
      rmse : Math.sqrt(seSum / n)
    };
  }
}



export default BasePredictor;